import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal, SectionLabel } from "@/lib/Reveal";

export const FAQS = [
  {
    q: "How much does a boiler service cost?",
    a: "A full annual boiler service is a fixed £85.99 — an 18-point safety check, flue gas analysis and certificate included. Bundle it with a cooker or hob service for £105 for both.",
  },
  {
    q: "Do you charge a call-out fee for breakdowns?",
    a: "Our emergency breakdown callout is £95.99 and includes the first hour of diagnostics and repair work on boilers, radiators, fires, cookers and hobs. Plumbing breakdowns are the same fixed price.",
  },
  {
    q: "Which areas do you cover?",
    a: "Exeter, Newton Abbot, Teignmouth, Bovey Tracey, Chudleigh, Torquay, Paignton, Brixham, Ashburton, Liverton, Dawlish and all surrounding areas across South Devon.",
  },
  {
    q: "Are you Gas Safe registered?",
    a: "Yes — Teign Gas is Gas Safe registered (number 951592) and every engineer carries verifiable Gas Safe ID. Always ask to see it; we're happy to show it.",
  },
  {
    q: "How quickly can you get to a breakdown?",
    a: "We run a 24/7 emergency response across South Devon. Most emergency callouts are attended the same day — book online or call 07737 016265 any time.",
  },
  {
    q: "Do you do plumbing as well as heating?",
    a: "Yes — all domestic plumbing: toilets that won't flush, basins, sinks, taps, leaks, bursts, outside taps, and new radiators supplied and fitted. Plumbing breakdowns are £95.99 including the first hour.",
  },
  {
    q: "How much is a new boiler installed?",
    a: "New boiler installations start from £1,799 fully fitted — supply, professional installation by Gas Safe engineers, system flush, registration and up to a 10-year manufacturer warranty. Every install gets a free fixed quote first.",
  },
  {
    q: "How do I book and pay?",
    a: "Pick your service, choose a day and time window, and pay securely by card online — it takes under two minutes. You can also call, WhatsApp, or request a free quote for bigger jobs.",
  },
  {
    q: "What is power flushing and do I need it?",
    a: "A power flush pumps cleaning chemicals through your radiators and pipework at high velocity to clear years of sludge. Signs you need one: cold spots on radiators, a noisy or frequently locking-out boiler, and rooms that never get warm. Prices start from £350.",
  },
  {
    q: "Do you install and service heat pumps?",
    a: "Yes — we fit and service air source heat pumps across South Devon. Suitability depends on your home's insulation and radiators, so every heat pump job starts with a free survey and fixed quote.",
  },
];

export const Faq = () => {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" data-testid="faq-section" className="py-20 lg:py-28">
      <div className="max-w-4xl mx-auto px-4 sm:px-8">
        <Reveal className="text-center mb-12">
          <SectionLabel>11 / Questions, answered</SectionLabel>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white mt-4">
            Before you ask.
          </h2>
        </Reveal>
        <div className="divide-y divide-neutral-800 border-y border-neutral-800">
          {FAQS.map((f, i) => (
            <Reveal key={f.q} delay={i * 0.05}>
              <div>
                <button
                  onClick={() => setOpen(open === i ? -1 : i)}
                  data-testid={`faq-question-${i}`}
                  className="w-full flex items-center justify-between gap-4 py-5 text-left group"
                >
                  <span className={`font-display font-semibold text-base sm:text-lg transition-colors ${open === i ? "text-torch" : "text-white group-hover:text-torch"}`}>
                    {f.q}
                  </span>
                  <Plus className={`w-5 h-5 shrink-0 text-torch transition-transform duration-300 ${open === i ? "rotate-45" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {open === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="pb-6 text-sm sm:text-base text-neutral-400 leading-relaxed max-w-2xl" data-testid={`faq-answer-${i}`}>
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
